<template>
    <div id="mail-detail-integration">
        <div class="section-title">
            {{ $t("mail.integrationForwardingSettingsTitle") }}
        </div>

        <div class="integration-card">
            <div class="integration-header">
                <div class="integration-icon">
                    <MailIcon />
                </div>
                <div class="integration-info">
                    <div class="integration-name">{{ $t("mail.integrationGmailForwardTitle") }}</div>
                    <div class="integration-description">{{ $t("mail.integrationGmailForwardDesc") }}</div>
                </div>
            </div>
            <div class="integration-body">
                <div class="integration-button" @click="openGmailModal">
                    <span class="material-icons">info</span>
                    {{ $t("mail.integrationHowToForward") }}
                </div>
                <div class="integration-items">
                    <div
                        class="integration-item"
                        v-for="address in forwardAddresses"
                        :key="address._id"
                    >
                        <div class="item-icon">
                            <MailIcon class="icon-mail" />
                        </div>
                        <a-tooltip :title="address.mail">
                            <div class="item-mail">{{ address.mail }}</div>
                        </a-tooltip>
                        <div class="item-status">
                            <span class="material-icons status-icon verified">verified</span>
                        </div>
                        <div class="item-actions">
                            <span
                                class="material-icons action-icon delete"
                                @click="confirmDeleteForward(address)"
                            >
                                delete
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="integration-card">
            <div class="integration-header">
                <div class="integration-icon">
                    <MicrosoftIcon />
                </div>
                <div class="integration-info">
                    <div class="integration-name">{{ $t("mail.integrationOutlookForwardTitle") }}</div>
                    <div class="integration-description">{{ $t("mail.integrationOutlookForwardDesc") }}</div>
                </div>
            </div>
            <div class="integration-body">
                <div class="integration-button" @click="openOutlookModal">
                    <span class="material-icons">info</span>
                    {{ $t("mail.integrationHowToForward") }}
                </div>
                <div class="integration-items">
                    <div class="integration-item new-item" @click="openForwardModal('microsoft')">
                        <span class="material-icons">add_circle</span>
                        <span>{{ $t("mail.integrationAddManualForward") }}</span>
                    </div>
                    <div
                        class="integration-item"
                        v-for="address in microsoftForwardAddresses"
                        :key="address._id"
                    >
                        <div class="item-icon">
                            <MailIcon class="icon-mail" />
                        </div>
                        <a-tooltip :title="address.mail">
                            <div class="item-mail">{{ address.mail }}</div>
                        </a-tooltip>
                        <div class="item-status">
                            <span class="material-icons status-icon verified">verified</span>
                        </div>
                        <div class="item-actions">
                            <span
                                class="material-icons action-icon delete"
                                @click="confirmDeleteForward(address)"
                            >
                                delete
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="integration-card">
            <div class="integration-header">
                <div class="integration-icon">
                    <span class="material-icons">mail</span>
                </div>
                <div class="integration-info">
                    <div class="integration-name">{{ $t("mail.integrationOtherProvidersTitle") }}</div>
                    <div class="integration-description">{{ $t("mail.integrationOtherProvidersDesc") }}</div>
                </div>
            </div>
            <div class="integration-body">
                <div class="integration-items">
                    <div class="integration-item new-item" @click="openForwardModal('other')">
                        <span class="material-icons">add_circle</span>
                        <span>{{ $t("mail.integrationAddManualForward") }}</span>
                    </div>
                    <div
                        class="integration-item"
                        v-for="address in otherForwardAddresses"
                        :key="address._id"
                    >
                        <div class="item-icon">
                            <MailIcon class="icon-mail" />
                        </div>
                        <a-tooltip :title="address.mail">
                            <div class="item-mail">{{ address.mail }}</div>
                        </a-tooltip>
                        <div class="item-status">
                            <span class="material-icons status-icon verified">verified</span>
                        </div>
                        <div class="item-actions">
                            <span
                                class="material-icons action-icon delete"
                                @click="confirmDeleteForward(address)"
                            >
                                delete
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modaller -->
        <Modal2
            v-if="isGmailModalVisible"
            :title="$t('mail.integrationGmailStepsTitle')"
            @cancel="closeGmailModal"
            :show-footer="false"
            :show-title="false"
            class="mail-instruction-modal"
        >
            <div class="modal-header">
                <h3>{{ $t("mail.integrationGmailStepsTitle") }}</h3>
            </div>
            <div class="modal-video-container">
                <GifPlayer
                    src="https://static.supsis.live/common/email/screenshot/gmail.mp4"
                    :autoplay="true"
                    :loop="true"
                    :muted="true"
                    :controls="false"
                    width="100%"
                    height="auto"
                ></GifPlayer>
            </div>
            <div class="modal-footer">
                <SupsisButton primary @click="closeGmailModal">{{ $t("mail.modalOk") }}</SupsisButton>
            </div>
        </Modal2>

        <Modal2
            v-if="isOutlookModalVisible"
            :title="$t('mail.integrationOutlookStepsTitle')"
            @cancel="closeOutlookModal"
            :show-footer="false"
            :show-title="false"
            class="mail-instruction-modal"
        >
            <div class="modal-header">
                <h3>{{ $t("mail.integrationOutlookStepsTitle") }}</h3>
            </div>
            <div class="modal-video-container">
                <GifPlayer
                    src="https://static.supsis.live/common/email/screenshot/outlook.mp4"
                    :autoplay="true"
                    :loop="true"
                    :muted="true"
                    :controls="false"
                    width="100%"
                    height="auto"
                ></GifPlayer>
            </div>
            <div class="modal-footer">
                <SupsisButton primary @click="closeOutlookModal">{{ $t("mail.modalOk") }}</SupsisButton>
            </div>
        </Modal2>

        <Modal2
            v-if="showForwardModal"
            :title="$t('mail.integrationAddForwardTitle')"
            @cancel="handleForwardModalCancel"
            :show-footer="false"
            :show-title="false"
            class="mail-forward-modal"
        >
            <div class="modal-header">
                <h3>{{ $t("mail.integrationAddForwardHeading", { type: forwardForm.type.toUpperCase() }) }}</h3>
            </div>
            <div class="modal-form">
                <div class="form-description">
                    {{ $t("mail.integrationForwardFormHelp") }}
                </div>
                <div class="form-group">
                    <label for="sourceEmail">{{ $t("mail.integrationSourceEmailLabel") }}</label>
                    <div class="input-container" :class="{'error': forwardForm.sourceEmail && !isValidSourceEmail}">
                        <span class="input-icon">
                            <span class="material-icons">mail</span>
                        </span>
                        <input
                            type="email"
                            v-model="forwardForm.sourceEmail"
                            id="sourceEmail"
                            :placeholder="$t('mail.integrationEmailExamplePlaceholder')"
                            required
                        />
                    </div>
                    <div class="error-message" v-if="forwardForm.sourceEmail && !isValidSourceEmail">
                        {{ $t("mail.integrationInvalidEmail") }}
                    </div>
                </div>
                <div class="form-group">
                    <label for="destinationEmail">{{ $t("mail.integrationDestinationLabel") }}</label>
                    <div class="input-container">
                        <span class="input-icon">
                            <span class="material-icons">forward_to_inbox</span>
                        </span>
                        <input
                            type="email"
                            v-model="forwardForm.destinationEmail"
                            id="destinationEmail"
                            readonly
                        />
                    </div>
                </div>
                <div class="form-group">
                    <label for="owner">{{ $t("mail.integrationOwnerLabel") }}</label>
                    <div class="input-container">
                        <span class="input-icon">
                            <span class="material-icons">person</span>
                        </span>
                        <input
                            type="text"
                            v-model="forwardForm.owner"
                            id="owner"
                            readonly
                        />
                    </div>
                </div>
                <div class="modal-footer">
                    <SupsisButton @click="handleForwardModalCancel">{{ $t("mail.cancel") }}</SupsisButton>
                    <SupsisButton
                        primary
                        :disabled="!isFormValid"
                        @click="handleFormModalSave"
                    >
                        {{ $t("common.save") }}
                    </SupsisButton>
                </div>
            </div>
        </Modal2>
    </div>
</template>

<script>
import Vue from "vue";
import { mapState, mapGetters, mapActions } from "vuex";
import { log2 } from "../../../../utils/log2";
import MailIcon from "../icons/MailIcon.vue";
import MicrosoftIcon from "../icons/MicrosoftIcon.vue";
import Modal2 from "../../../../components/common/Modal2.vue";
import GifPlayer from "../../../../components/common/GifPlayer.vue";
import SupsisButton from "../../../Pages/Settings/components/SupsisButton.vue";
import { MAIL_API } from "../../../../api/mail";
import { sendSocketMessage } from "../../../../utils/socketStream";
import CONFIG from "../../../../config/config";

const log = log2("EmailIntegration.vue");

// Email validasyon fonksiyonu
const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

export default Vue.extend({
    name: "EmailIntegration",
    components: {
        MailIcon,
        MicrosoftIcon,
        Modal2,
        SupsisButton,
        GifPlayer
    },
    props: {
        initialTab: {
            type: String,
            default: "forwarding"
        }
    },
    data() {
        return {
            selectedId: this.$route.params.id,
            selectedMail: "",
            isGmailModalVisible: false,
            isOutlookModalVisible: false,
            emailDomainEnabled: true,
            forwardAddresses: [],
            microsoftForwardAddresses: [],
            otherForwardAddresses: [],
            isGmailForward: false,
            showForwardModal: false,
            forwardForm: {
                type: "gmail",
                sourceEmail: "",
                destinationEmail: "",
                owner: this.$store.state.auth.user?.name,
            }
        };
    },
    computed: {
        ...mapState({
            allMailAddresses: (state) => state.mail.allMailAddresses,
            site: (state) => state.site.site,
            user: (state) => state.auth.user,
            domainIsCreated: (state) => state.mail.domainIsCreated,
            emailDomain: (state) =>
                state.site.site.alias === "supsis"
                    ? CONFIG.EMAIL_DOMAIN
                    : `${state.site.site.alias}.${CONFIG.EMAIL_DOMAIN}`,
        }),
        ...mapGetters({
            checkPermission: "auth/checkPermission",
        }),
        isValidSourceEmail() {
            return isValidEmail(this.forwardForm.sourceEmail);
        },
        isFormValid() {
            return this.forwardForm.sourceEmail && this.isValidSourceEmail;
        }
    },
    methods: {
        ...mapActions({}),
        openGmailModal() {
            this.isGmailModalVisible = true;
        },
        closeGmailModal() {
            this.isGmailModalVisible = false;
        },
        openOutlookModal() {
            this.isOutlookModalVisible = true;
        },
        closeOutlookModal() {
            this.isOutlookModalVisible = false;
        },
        openForwardModal(platform) {
            this.forwardForm.type = platform;
            this.showForwardModal = true;
        },
        handleFormModalSave() {
            const selectedMail = this.allMailAddresses.find(
                (email) =>
                    email._id === this.selectedId || (email.department && email.department._id === this.selectedId)
            );
            const data = {
                mail: this.forwardForm.sourceEmail,
                forwardTo: this.forwardForm.destinationEmail,
                userId: selectedMail?.user?._id,
                forceForwardManual: true,
                platform: this.forwardForm.type,
            };
            log("handleFormModalSave", { data, selectedMail });
            this.showForwardModal = false;
            MAIL_API.createForwardEmailAddress(data)
                .then((res) => {
                    this.$message.success("Yönlendirme e-postası başarıyla eklendi");
                    this.$store.dispatch("mail/getAgentsMailAddressList");
                })
                .catch((err) => {
                    this.$message.error("Yönlendirme e-postası eklenirken hata oluştu");
                });
        },
        handleForwardModalCancel() {
            this.showForwardModal = false;
            this.forwardForm.sourceEmail = "";
        },
        confirmDeleteForward(address) {
            this.$confirm({
                title: "E-posta Adresini Sil",
                content: "Bu e-posta adresini silmek istediğinizden emin misiniz?",
                okText: "Evet",
                okType: "danger",
                cancelText: "Hayır",
                onOk: () => {
                    this.deleteForwardAddress(address);
                },
            });
        },
        deleteForwardAddress(address) {
            sendSocketMessage("email delete_user_email", {
                siteId: this.site._id,
                mailAddressId: address._id,
                identity: address.mail,
            });
            this.$message.success("E-posta adresi başarıyla silindi");
            this.forwardAddresses = this.forwardAddresses.filter((item) => item._id !== address._id);
            this.microsoftForwardAddresses = this.microsoftForwardAddresses.filter((item) => item._id !== address._id);
            this.otherForwardAddresses = this.otherForwardAddresses.filter((item) => item._id !== address._id);
        },
        updateMailAddressList() {
            const selectedMail = this.allMailAddresses.find(
                (email) =>
                    email._id === this.selectedId || (email.department && email.department._id === this.selectedId)
            );

            if (selectedMail) {
                this.selectedMail = selectedMail.mail;
                this.forwardForm.destinationEmail = selectedMail.mail;

                this.forwardAddresses = this.allMailAddresses.filter(
                    (email) =>
                        email.forwardTo === selectedMail.mail &&
                        !!email.isForward &&
                        !!email.isVerified &&
                        email.isForwardPlatform === "gmail"
                );

                this.microsoftForwardAddresses = this.allMailAddresses.filter(
                    (email) =>
                        email.forwardTo === selectedMail.mail &&
                        !!email.isForward &&
                        !!email.isVerified &&
                        email.isForwardPlatform === "microsoft"
                );

                this.otherForwardAddresses = this.allMailAddresses.filter(
                    (email) =>
                        email.forwardTo === selectedMail.mail &&
                        !!email.isForward &&
                        !!email.isVerified &&
                        email.isForwardPlatform === "other"
                );
            } else {
                this.selectedMail = "";
                this.forwardForm.destinationEmail = "";
                this.forwardAddresses = [];
                this.microsoftForwardAddresses = [];
                this.otherForwardAddresses = [];
            }
        },
    },
    mounted() {
        this.updateMailAddressList();
    },
    watch: {
        allMailAddresses: {
            handler() {
                this.updateMailAddressList();
            },
            deep: true
        }
    },
});
</script>

<style scoped lang="scss">
#mail-detail-integration {
    display: flex;
    flex-direction: column;
    background: var(--color-bg-default);
    position: relative;
    width: 100%;
    padding: 20px;

    .section-title {
        font-size: 20px;
        font-weight: 600;
        margin-bottom: 20px;
        color: #22272d;
    }

    .integration-card {
        background: #fff;
        border: 1px solid var(--color-border-default);
        border-radius: 8px;
        margin-bottom: 20px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

        .integration-header {
            display: flex;
            align-items: center;
            padding: 15px;
            border-bottom: 1px solid var(--color-border-default);
            background-color: #f9f9f9;

            .integration-icon {
                width: 40px;
                height: 40px;
                display: flex;
                align-items: center;
                justify-content: center;
                margin-right: 15px;

                .material-icons {
                    font-size: 24px;
                    color: #22272d;
                }
            }

            .integration-info {
                .integration-name {
                    font-size: 16px;
                    font-weight: 600;
                    margin-bottom: 4px;
                    color: #22272d;
                }

                .integration-description {
                    font-size: 14px;
                    color: #666;
                }
            }
        }

        .integration-body {
            padding: 15px;

            .integration-button {
                display: inline-flex;
                align-items: center;
                background-color: #f5f5f5;
                padding: 8px 12px;
                border-radius: 4px;
                cursor: pointer;
                margin-bottom: 15px;
                font-size: 14px;
                border: 1px solid #e0e0e0;

                .material-icons {
                    font-size: 18px;
                    margin-right: 8px;
                    color: #22272d;
                }

                &:hover {
                    background-color: #e8e8e8;
                }
            }

            .integration-items {
                display: flex;
                flex-wrap: wrap;
                gap: 15px;

                .integration-item {
                    position: relative;
                    width: 200px;
                    height: 120px;
                    background: #fff;
                    border: 1px solid #e0e0e0;
                    border-radius: 6px;
                    padding: 15px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;

                    &.new-item {
                        background-color: #f7f7f7;
                        border-style: dashed;
                        cursor: pointer;

                        span {
                            font-size: 14px;
                            font-weight: 500;
                            color: #22272d;
                        }

                        .material-icons {
                            font-size: 24px;
                            margin-bottom: 8px;
                            color: #22272d;
                        }

                        &:hover {
                            background-color: #f0f0f0;
                        }
                    }

                    &:hover {
                        box-shadow: 0 3px 8px rgba(0, 0, 0, 0.1);
                        transform: translateY(-2px);
                    }

                    .item-icon {
                        width: 40px;
                        height: 40px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        margin-bottom: 10px;
                    }

                    .item-mail {
                        font-size: 14px;
                        font-weight: 500;
                        text-align: center;
                        margin-bottom: 8px;
                        word-break: break-all;
                        max-width: 180px;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        color: #22272d;
                    }

                    .item-status {
                        display: flex;
                        align-items: center;
                        margin-bottom: 4px;

                        .status-icon {
                            font-size: 18px;

                            &.verified {
                                color: #52c41a;
                            }
                        }
                    }

                    .item-actions {
                        position: absolute;
                        top: 5px;
                        right: 5px;

                        .action-icon {
                            cursor: pointer;
                            font-size: 18px;
                            color: #999;

                            &:hover {
                                color: #666;
                            }

                            &.delete:hover {
                                color: #f5222d;
                            }
                        }
                    }
                }
            }
        }
    }

    .modal-header {
        padding: 15px 20px;
        border-bottom: 1px solid #e0e0e0;

        h3 {
            margin: 0;
            font-size: 18px;
            font-weight: 600;
            color: #22272d;
        }
    }

    .modal-footer {
        padding: 15px 20px;
        border-top: 1px solid #e0e0e0;
        display: flex;
        justify-content: flex-end;
        gap: 10px;
    }

    .modal-video-container {
        padding: 20px;
        display: flex;
        justify-content: center;
        width: 100%;
        max-width: 800px;
        margin: 0 auto;
    }

    .modal-form {
        padding: 20px;

        .form-description {
            margin-bottom: 20px;
            font-size: 14px;
            color: #666;
            background-color: #f9f9f9;
            padding: 15px;
            border-radius: 6px;
            border-left: 4px solid #22272d;
        }

        .form-group {
            margin-bottom: 24px;

            label {
                display: block;
                margin-bottom: 8px;
                font-weight: 500;
                color: #22272d;
                font-size: 14px;
            }

            .input-container {
                position: relative;
                display: flex;
                align-items: center;

                .input-icon {
                    position: absolute;
                    left: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;

                    .material-icons {
                        color: #22272d;
                        font-size: 18px;
                    }
                }

                input {
                    width: 100%;
                    padding: 12px 12px 12px 42px;
                    border: 1px solid #d9d9d9;
                    border-radius: 6px;
                    font-size: 14px;
                    transition: all 0.3s ease;

                    &:focus {
                        outline: none;
                        border-color: #22272d;
                        box-shadow: 0 0 0 2px rgba(34, 39, 45, 0.1);
                    }

                    &::placeholder {
                        color: #bbb;
                    }

                    &[readonly] {
                        background-color: #f5f5f5;
                        cursor: not-allowed;
                    }
                }
            }

            .error-message {
                color: #f5222d;
                font-size: 12px;
                margin-top: 4px;
            }
        }
    }
}
</style>

<style lang="scss">
.mail-forward-modal,
.mail-instruction-modal {
    .modal-form {
        padding: 20px;

        .form-description {
            margin-bottom: 20px;
            font-size: 14px;
            color: #666;
            background-color: #f9f9f9;
            padding: 15px;
            border-radius: 6px;
            border-left: 4px solid #22272d;
        }

        .form-group {
            margin-bottom: 24px;

            label {
                display: block;
                margin-bottom: 8px;
                font-weight: 500;
                color: #22272d;
                font-size: 14px;
            }

            .input-container {
                position: relative;
                display: flex;
                align-items: center;

                .input-icon {
                    position: absolute;
                    left: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;

                    .material-icons {
                        color: #22272d;
                        font-size: 18px;
                    }
                }

                input {
                    width: 100%;
                    padding: 12px 12px 12px 42px;
                    border: 1px solid #d9d9d9;
                    border-radius: 6px;
                    font-size: 14px;
                    transition: all 0.3s ease;

                    &:focus {
                        outline: none;
                        border-color: #22272d;
                        box-shadow: 0 0 0 2px rgba(34, 39, 45, 0.1);
                    }

                    &::placeholder {
                        color: #bbb;
                    }

                    &[readonly] {
                        background-color: #f5f5f5;
                        cursor: not-allowed;
                    }
                }
            }

            &.error {
                input {
                    border-color: #f5222d;

                    &:focus {
                        box-shadow: 0 0 0 2px rgba(245, 34, 45, 0.1);
                    }
                }

                .input-icon {
                    .material-icons {
                        color: #f5222d;
                    }
                }
            }
        }
    }

    .modal-header {
        padding: 15px 20px;
        border-bottom: 1px solid #e0e0e0;

        h3 {
            margin: 0;
            font-size: 18px;
            font-weight: 600;
            color: #22272d;
        }
    }

    .modal-footer {
        padding: 15px 20px;
        border-top: 1px solid #e0e0e0;
        display: flex;
        justify-content: flex-end;
        gap: 10px;
    }

    .modal-video-container {
        padding: 20px;
        display: flex;
        justify-content: center;
        width: 100%;
        max-width: 800px;
        margin: 0 auto;

        .gif-player-container {
            width: 100%;
        }
    }
}
</style>
